import { useState, useEffect, useCallback } from 'react'
import { useNotifications } from '../../contexts/NotificationContext'
import { useHaptics } from '../../utils/haptics'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'
import { accountApi } from '../../utils/accountApi'

// Icons
function CreditCardIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="1" y="4" width="22" height="16" rx="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  )
}

function PlusIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 5v14M5 12h14"/>
    </svg>
  )
}

function TrashIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
    </svg>
  )
}

function SmartphoneIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="5" y="2" width="14" height="20" rx="2"/>
      <line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
  )
}

const TYPE_LABEL = { CREDIT_CARD: 'Card', BANK_ACCOUNT: 'Bank account', DIGITAL_WALLET: 'Wallet' }
const NOT_AVAILABLE = 'Adding a payment method in the app is not available yet'

export default function PaymentMethods() {
  const { showSuccess, showError } = useNotifications()
  const haptics = useHaptics()
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadPaymentMethods = useCallback(async () => {
    try {
      setPaymentMethods(await accountApi.paymentMethods())
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadPaymentMethods() }, [loadPaymentMethods])

  const getCardBrandIcon = (brand) => {
    const brandColors = {
      visa: 'text-blue-600',
      mastercard: 'text-red-600',
      amex: 'text-green-600',
      discover: 'text-orange-600'
    }
    return brandColors[String(brand || '').toLowerCase()] || 'text-slate-600'
  }

  const handleSetDefault = async (methodId) => {
    haptics.light()
    try {
      setPaymentMethods(await accountApi.setDefaultPaymentMethod(methodId))
      showSuccess('Default payment method updated')
    } catch (e) {
      showError(e.message)
    }
  }

  const handleRemove = async (method) => {
    if (!window.confirm('Remove this payment method from your account?')) return
    haptics.light()
    try {
      setPaymentMethods(await accountApi.removePaymentMethod(method.id))
      showSuccess('Payment method removed')
    } catch (e) {
      showError(e.message)
    }
  }

  const headerAction = (
    <button disabled title={NOT_AVAILABLE} className="p-2 bg-slate-900 text-white rounded-xl opacity-50 cursor-not-allowed">
      <PlusIcon />
    </button>
  )

  return (
    <PageLayout
      title="Payment Methods"
      subtitle="Manage your payment options"
      headerAction={headerAction}
      onRefresh={loadPaymentMethods}
      showBackButton={true}
    >
      {/* Payment Methods List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
            </div>
          </Card>
        ) : error ? (
          <Card><div className="text-sm text-red-600">Could not load payment methods: {error}</div></Card>
        ) : paymentMethods.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <CreditCardIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-900 mb-2">No Payment Methods</h3>
              <p className="text-slate-600 text-sm">You have no saved payment methods.</p>
            </div>
          </Card>
        ) : (
          paymentMethods.map((method) => (
            <Card key={method.id} hover>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    method.type === 'CREDIT_CARD' ? 'bg-slate-100' : 'bg-blue-100'
                  }`}>
                    {method.type === 'CREDIT_CARD' ? (
                      <CreditCardIcon className={getCardBrandIcon(method.provider)} />
                    ) : (
                      <SmartphoneIcon className="text-blue-600" />
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-slate-900">
                        {method.last_four ? `•••• ${method.last_four}` : TYPE_LABEL[method.type] || method.type}
                      </div>
                      {method.is_default && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600">
                      {[method.provider, TYPE_LABEL[method.type],
                        method.expiry_month && method.expiry_year ? `Expires ${String(method.expiry_month).padStart(2, '0')}/${method.expiry_year}` : null
                      ].filter(Boolean).join(' • ')}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!method.is_default && (
                    <button
                      onClick={() => handleSetDefault(method.id)}
                      className="px-3 py-1 text-xs border border-slate-300 rounded-lg hover:bg-slate-50 touch-manipulation"
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    onClick={() => handleRemove(method)}
                    title="Remove"
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg touch-manipulation"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Adding cards */}
      <Card>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
            <PlusIcon className="text-slate-500" />
          </div>
          <div>
            <div className="font-medium text-slate-900 mb-1">Add a payment method</div>
            <div className="text-sm text-slate-600">
              {NOT_AVAILABLE}. Only the last four digits of a saved method are ever shown here; full card numbers are never stored in the app.
            </div>
          </div>
        </div>
      </Card>
    </PageLayout>
  )
}
